import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import Icon from './Icon.jsx'

/**
 * The one modal shell everything else builds on: a new person, a new job type, a new CAT
 * team, a new display, the mode switch. Centralising the backdrop, the close button and the
 * Escape handler means every one of those got the same behaviour for free instead of five
 * slightly different hand-rolled versions.
 *
 * Portalled to document.body rather than rendered in place: the topbar (home to UserMenu,
 * where several of these open from) uses backdrop-filter for its own frosted-glass look, and
 * backdrop-filter on an ancestor creates a new containing block for position:fixed
 * descendants — so a modal rendered inline there had its "full-screen" backdrop silently
 * clamped to the topbar's own ~50px strip. Confirmed live: everything below that strip was
 * fully clickable through what looked like a modal. A portal renders outside that ancestor
 * entirely, so position:fixed always resolves against the real viewport.
 */
// Open modals, oldest first. Every one of them listens for Escape on the document, so a
// modal opened OVER another used to close both at once — one keypress and you were back two
// screens, having meant to dismiss one. Escape now reaches only the last one opened, which
// is the one in front of the person pressing it.
const stack = []

export default function Modal({ title, onClose, children, width = 480, className = '' }) {
  // Read through a ref so the effect below can depend on nothing: callers pass an inline
  // arrow, and re-running on every render would push this modal back to the top of the
  // stack and hand it an Escape meant for the one actually in front.
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    const token = {}
    stack.push(token)
    const onKey = (e) => {
      if (e.key === 'Escape' && stack[stack.length - 1] === token) closeRef.current()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      const i = stack.indexOf(token)
      if (i !== -1) stack.splice(i, 1)
    }
    // Mount and unmount only — the stack has to reflect the order modals were OPENED in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className={`modal ${className}`}
        style={{ maxWidth: width }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}
